import { jsxs, jsx } from "react/jsx-runtime";
import { colors, s4, s16, r4, s24 } from "@ardoq/design-tokens";
import styled, { css } from "styled-components";
import { Icon, IconSize, IconName } from "@ardoq/icons";
import { focusRingCSS, ellipsisCSS } from "@ardoq/style-helpers";
const INTER_FONT_FAMILY = [
  "Inter",
  "-apple-system",
  "Segoe UI",
  "Roboto",
  "Noto Sans",
  "Ubuntu",
  "Cantarell",
  "Helvetica Neue"
].join(", ");
const ARDOQ_DEFAULT_FONT_FAMILY = INTER_FONT_FAMILY;
const normal14 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
`;
const normal14Italic = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-style: italic;
  font-size: 14px;
  line-height: 16px;
`;
const normal14Uppercase = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 14px;
  text-transform: uppercase;
  line-height: 16px;
`;
const normal16Italic = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-style: italic;
  font-size: 16px;
  line-height: 24px;
`;
const normal16 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;
const normal16Uppercase = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 16px;
  text-transform: uppercase;
  line-height: 24px;
`;
const normal20 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 20px;
  line-height: 24px;
`;
const normal22 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 22px;
  line-height: 24px;
`;
const semibold14 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 14px;
  line-height: 16px;
`;
const semibold16 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
`;
const semibold40 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 40px;
`;
const fontMixins = {
  normal14,
  normal14Italic,
  normal14Uppercase,
  normal16Italic,
  normal16,
  normal16Uppercase,
  normal20,
  normal22,
  semibold14,
  semibold16,
  semibold40
};
const header1 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 26px;
  line-height: 28px;
  letter-spacing: -0.5px;
`;
const header2 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
`;
const header3 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
`;
const header4 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
`;
const header5 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  text-transform: uppercase;
`;
const button = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 600;
  letter-spacing: 0.5px;
  font-size: 16px;
  line-height: 24px;
`;
const text1 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 16px;
  line-height: 20px;
`;
const text1Italic = `
  ${text1};
  font-style: italic;
`;
const text1Bold = `
  ${text1};
  font-weight: 600;
`;
const text2 = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
`;
const text2Bold = `
  ${text2}
  font-weight: 600;
`;
const caption = `
  font-family: ${ARDOQ_DEFAULT_FONT_FAMILY};
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
`;
const typographyMixins = {
  header1,
  header2,
  header3,
  header4,
  header5,
  button,
  text1,
  text1Italic,
  text1Bold,
  text2,
  text2Bold,
  caption
};
const dashedGradient = (color) => css`
  background-image: linear-gradient(
    to right,
    ${color} 33%,
    rgba(255, 255, 255, 0) 0%
  );
`;
const underlinedElStyle = css`
  cursor: default;
  position: relative;
  display: inline-block;
`;
const underlineStyle = css`
  content: ' ';
  display: block;
  width: 100%;
  bottom: 0;
  position: absolute;
  left: 0;
  height: 2px;
`;
const underline = css`
  ${underlinedElStyle}
  &::after {
    ${underlineStyle}
  }
`;
const popoverUnderline = css`
  ${underline};
  &::after {
    ${dashedGradient(colors.grey35)}
    background-position: bottom;
    background-size: 4px 1px;
    background-repeat: repeat-x;
  }
  &:hover {
    &::after {
      ${dashedGradient(colors.grey25)}
    }
  }
`;
const AS_LINK_LR_PADDING = 4;
const asLinkUnderline = (color, type = "primary") => css`
  text-decoration: underline ${type === "dotted" ? "dotted" : "solid"}
    ${color ?? colors.surfaceActionPrimary} 1px;
  text-underline-offset: ${s4};
  padding-bottom: ${s4};
  margin-bottom: -${s4};
`;
const linkStyledIconCss = css`
  display: inline;
  min-width: fit-content;
  vertical-align: middle;
`;
const AsLinkStyledIconNormal = styled(Icon).attrs({
  iconName: IconName.OPEN_IN_NEW,
  iconSize: IconSize.REGULAR
})`
  ${linkStyledIconCss}
  margin-left: ${AS_LINK_LR_PADDING}px;
`;
const AsLinkStyledIconSmall = styled(AsLinkStyledIconNormal).attrs({
  iconSize: 18
})`
  padding: 1px 4px 1px 0;
`;
const initialColorsByType = {
  primary: colors.textAction,
  secondary: colors.textModerate,
  dotted: colors.textModerate,
  primaryButton: colors.textOnInvertedStrong
};
const initialBackgroundColorsByType = {
  primaryButton: colors.surfaceActionPrimary
};
const hoverColorsByType = {
  primary: colors.textActionHover,
  secondary: colors.textDefault,
  dotted: colors.textDefault,
  primaryButton: colors.textOnInvertedStrong
};
const hoverBackgroundColorsByType = {
  primaryButton: colors.surfaceActionPrimaryHover
};
const asLinkButton = (type = "primary") => css`
  background: ${type ? initialBackgroundColorsByType[type] : underline};
  padding: ${s4} ${s16};
  border: 1px solid transparent;
  border-radius: ${r4};
  line-height: ${s24};
  font-weight: 600;
  &:hover {
    background: ${type ? hoverBackgroundColorsByType[type] : underline};
  }
`;
const isLinkButton = (type = "primary") => ["primaryButton"].includes(type);
const AsLinkTextContainer = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;
const AsLinkContainer = styled.a`
  max-width: 100%;
  align-items: center;
  cursor: pointer;

  position: relative;
  &:focus {
    outline: none;
  }

  ${({ $color, $type }) => css`
    color: ${$color ?? ($type ? initialColorsByType[$type] : colors.textAction)};
    &:hover {
      color: ${$color ?? ($type ? hoverColorsByType[$type] : colors.textAction)};
    }
  `};
  ${({ $typography = "text1" }) => typographyMixins[$typography]}
  ${({ $type = "primary" }) => isLinkButton($type) ? asLinkButton($type) : ""}
  ${({ $type }) => $type && css`
      &:focus-visible {
        outline: none;
        ${focusRingCSS};
      }
    `};

  ${({ $underlineOnHover, $noUnderline, $color, $type = "primary" }) => !isLinkButton($type) && css`
      color: ${$color ?? initialColorsByType[$type]};
      ${AsLinkTextContainer} {
        ${$underlineOnHover || $noUnderline ? "" : asLinkUnderline($color ?? initialColorsByType[$type], $type)}
      }
      &:hover {
        color: ${$color ?? hoverColorsByType[$type]};
        ${AsLinkTextContainer} {
          ${!$noUnderline && asLinkUnderline($color ?? hoverColorsByType[$type], $type)}
        }
      }
    `}
`;
const AsLinkIcon = ({ small, hideIcon }) => {
  if (hideIcon) return null;
  if (small) return /* @__PURE__ */ jsx(AsLinkStyledIconSmall, {});
  return /* @__PURE__ */ jsx(AsLinkStyledIconNormal, {});
};
const LinkStyledIcon = styled(Icon)`
  ${linkStyledIconCss}
  margin-left: 4px;
`;
const LinkIcon = ({
  typography,
  iconName
}) => /* @__PURE__ */ jsx(
  LinkStyledIcon,
  {
    iconSize: typography === "caption" ? 12 : IconSize.SMALL,
    iconName: iconName ?? IconName.OPEN_IN_NEW
  }
);
const AsLink = ({
  small,
  underlineOnHover,
  hideIcon,
  children,
  color,
  ...rest
}) => {
  const typography = small ? "text2" : "text1";
  const ownProps = { small, underlineOnHover, hideIcon, color };
  return /* @__PURE__ */ jsxs(
    AsLinkContainer,
    {
      ...rest,
      $color: color,
      $typography: typography,
      $underlineOnHover: underlineOnHover,
      children: [
        /* @__PURE__ */ jsx(AsLinkTextContainer, { children }),
        /* @__PURE__ */ jsx(AsLinkIcon, { ...ownProps })
      ]
    }
  );
};
const Link = ({
  typography = "text1",
  underlineOnHover,
  noUnderline,
  hideIcon,
  children,
  color,
  href,
  target,
  type = "primary",
  rel,
  onClick,
  iconName,
  ...rest
}) => {
  return /* @__PURE__ */ jsxs(
    AsLinkContainer,
    {
      ...rest,
      $color: color,
      $typography: typography,
      $type: type,
      $underlineOnHover: underlineOnHover,
      $noUnderline: noUnderline,
      target: target && href ? target : void 0,
      rel: rel && href ? rel : void 0,
      onClick: (e) => {
        if (onClick) {
          e.preventDefault();
          onClick(e);
        }
      },
      href: href || "#",
      children: [
        /* @__PURE__ */ jsx(AsLinkTextContainer, { children }),
        !hideIcon && /* @__PURE__ */ jsx(LinkIcon, { typography, iconName })
      ]
    }
  );
};
const StyledText = styled.div`
  ${({ $color }) => $color && css`
      color: ${colors[$color]};
    `}
  ${({ $variant }) => $variant && typographyMixins[$variant]}
  ${({ $marginReset }) => $marginReset && marginResetMixin}
  ${({ $align }) => $align && `text-align: ${$align};`}
  ${({ $truncate }) => $truncate && ellipsisCSS}
`;
const marginResetMixin = `
  margin: 0;
`;
const Header1 = ({ color, align, ...rest }) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      as: "h1",
      $variant: "header1",
      $marginReset: true,
      $align: align,
      $color: color,
      ...rest
    }
  );
};
const Header2 = ({ color, align, ...rest }) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      as: "h2",
      $variant: "header2",
      $marginReset: true,
      $align: align,
      $color: color,
      ...rest
    }
  );
};
const Header3 = ({ color, align, ...rest }) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      as: "h3",
      $variant: "header3",
      $marginReset: true,
      $align: align,
      $color: color,
      ...rest
    }
  );
};
const Header4 = ({ color, align, ...rest }) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      as: "h4",
      $variant: "header4",
      $marginReset: true,
      $align: align,
      $color: color,
      ...rest
    }
  );
};
const Header5 = ({ color, align, ...rest }) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      as: "h5",
      $variant: "header5",
      $marginReset: true,
      $align: align,
      $color: color,
      ...rest
    }
  );
};
const textColorTokenList = [
  "textDefault",
  "textModerate",
  "textSubtle",
  "textDisabled",
  "textOnInverted",
  "textOnInvertedSubtle",
  "textOnInvertedStrong",
  "textOnInvertedDisabled",
  "textError",
  "textSuccess",
  "textInfo",
  "textAction",
  "textWarning"
];
const InlineText = ({
  color,
  variant,
  align,
  truncate,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      $color: color,
      $variant: variant,
      $align: align,
      $truncate: truncate,
      ...rest,
      as: "span"
    }
  );
};
const Paragraph = ({ color, variant, align, ...rest }) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      $color: color,
      $variant: variant,
      $align: align,
      $marginReset: true,
      ...rest,
      as: "p"
    }
  );
};
const Text = ({
  color,
  variant,
  align,
  truncate,
  ...rest
}) => {
  return /* @__PURE__ */ jsx(
    StyledText,
    {
      $color: color,
      $variant: variant,
      $align: align,
      $truncate: truncate,
      ...rest,
      as: "div"
    }
  );
};
export {
  ARDOQ_DEFAULT_FONT_FAMILY,
  AsLink,
  Header1,
  Header2,
  Header3,
  Header4,
  Header5,
  INTER_FONT_FAMILY,
  InlineText,
  Link,
  Paragraph,
  Text,
  button,
  caption,
  fontMixins,
  header1,
  header2,
  header3,
  header4,
  header5,
  normal14,
  normal14Italic,
  normal14Uppercase,
  normal16,
  normal16Italic,
  normal16Uppercase,
  normal20,
  normal22,
  popoverUnderline,
  semibold14,
  semibold16,
  semibold40,
  text1,
  text1Bold,
  text1Italic,
  text2,
  text2Bold,
  textColorTokenList,
  typographyMixins
};
//# sourceMappingURL=index.js.map
